import React from "react";
import {
  AUTH_PREFIX_PATH,
  APP_PREFIX_PATH,
  EXTERNAL_PREFIX_PATH,
  APP_NOADMIN_PREFIX_PATH,
} from "configs/AppConfig";
import useUserPermissions from "tools/validateAccess";

const externalRoutes = [
  {
    key: "external.order-by-id",
    path: `${EXTERNAL_PREFIX_PATH}/order/:id`,
    component: React.lazy(() =>
      import("views/app-external-views/orders/views/order-detail")
    ),
  },
];

export const publicRoutes = [
  {
    key: "login",
    path: `${AUTH_PREFIX_PATH}/login`,
    component: React.lazy(() =>
      import("views/auth-views/authentication/login")
    ),
  },
  {
    key: "register",
    path: `${AUTH_PREFIX_PATH}/register/:id`,
    component: React.lazy(() =>
      import("views/auth-views/authentication/register-free")
    ),
  },
  {
    key: "forgot-password",
    path: `${AUTH_PREFIX_PATH}/forgot-password`,
    component: React.lazy(() =>
      import("views/auth-views/authentication/forgot-password")
    ),
  },
  {
    key: "newuser-password",
    path: `${AUTH_PREFIX_PATH}/newuser-password`,
    component: React.lazy(() =>
      import("views/auth-views/authentication/newuser-password")
    ),
  },

  {
    key: "no-access-permission",
    path: `${AUTH_PREFIX_PATH}/no-access-permission`,
    component: React.lazy(() =>
      import("views/app-admin-views/no-permission/views/")
    ),
  },
  ...externalRoutes,
];

export const adminRoutes = [
  {
    key: "apps.buy-plan",
    path: `${APP_PREFIX_PATH}/buy-plans`,
    component: React.lazy(() =>
      import("views/app-shared-views/plans/views/buy-plans/BuyPlans")
    ),
  },
  {
    key: "apps.upgrade-plan",
    path: `${APP_PREFIX_PATH}/upgrade-plans`,
    component: React.lazy(() =>
      import("views/app-shared-views/plans/views/upgrade-plans/UpgradePlans")
    ),
  },
  {
    key: "apps.surplus-admin",
    path: `${APP_PREFIX_PATH}/surplus-admin`,
    component: React.lazy(() =>
      import("views/app-shared-views/plan-surplus/views/surplus-admin")
    ),
  },
  {
    key: "apps.surplus-noadmin",
    path: `${APP_PREFIX_PATH}/surplus-noadmin`,
    component: React.lazy(() =>
      import("views/app-shared-views/plan-surplus/views/surplus-transactional")
    ),
  },
  {
    key: "apps.start",
    path: `${APP_PREFIX_PATH}/start/*`,
    component: React.lazy(() => import("views/app-noadmin-views/start")),
    meta: {
      blankLayout: true,
    },
  },
  {
    key: "apps.start.shift-start",
    path: `${APP_PREFIX_PATH}/start/shift-start`,
    component: React.lazy(() =>
      import("views/app-noadmin-views/start/shift-start")
    ),
    meta: {
      blankLayout: true,
    },
  },
  {
    key: "app.locations",
    path: `${APP_PREFIX_PATH}/locations/*`,
    component: React.lazy(() => import("views/app-admin-views/locations")),
  },
  {
    key: "apps.locations.add-locations",
    path: `${APP_PREFIX_PATH}/locations/add-locations`,
    component: React.lazy(() =>
      import("views/app-admin-views/locations/add-locations")
    ),
  },
  {
    key: "apps.locations.edit-locations",
    path: `${APP_PREFIX_PATH}/locations/edit-locations/:id`,
    component: React.lazy(() =>
      import("views/app-admin-views/locations/edit-locations")
    ),
  },
  {
    key: "apps.locations.journal",
    path: `${APP_PREFIX_PATH}/locations/journal/:id`,
    component: React.lazy(() =>
      import("views/app-admin-views/locations/journal")
    ),
  },
  {
    key: "apps.locations.cashdrawers-list",
    path: `${APP_PREFIX_PATH}/locations/list-cashdrawers/:id`,
    component: React.lazy(() =>
      import("views/app-admin-views/locations/list-cashdrawers")
    ),
  },
  {
    key: "apps.locations.locations-list",
    path: `${APP_PREFIX_PATH}/locations/list-locations`,
    component: React.lazy(() =>
      import("views/app-admin-views/locations/list-locations")
    ),
  },
  {
    key: "app.users",
    path: `${APP_PREFIX_PATH}/users/*`,
    component: React.lazy(() => import("views/app-admin-views/users")),
  },
  {
    key: "apps.users.add-users",
    path: `${APP_PREFIX_PATH}/users/add-users`,
    component: React.lazy(() =>
      import("views/app-admin-views/users/add-users")
    ),
  },
  {
    key: "apps.users.add-users.masive-upload",
    path: `${APP_PREFIX_PATH}/users/masive-upload`,
    component: React.lazy(() =>
      import("views/app-admin-views/users/masive-upload/masive-upload")
    ),
  },
  {
    key: "apps.users.edit-users",
    path: `${APP_PREFIX_PATH}/users/edit-users/:id`,
    component: React.lazy(() =>
      import("views/app-admin-views/users/edit-users")
    ),
  },
  {
    key: "apps.users.assign-users-locations",
    path: `${APP_PREFIX_PATH}/users/assign-users-locations/:id`,
    component: React.lazy(() =>
      import("views/app-admin-views/users/assign-users-locations")
    ),
  },
  {
    key: "apps.users.users-list",
    path: `${APP_PREFIX_PATH}/users/list-users`,
    component: React.lazy(() =>
      import("views/app-admin-views/users/list-users")
    ),
  },
  {
    key: "apps.users.commissions",
    path: `${APP_PREFIX_PATH}/users/commissions`,
    component: React.lazy(() =>
      import("views/app-admin-views/users/commissions")
    ),
  },
  {
    key: "apps.users.roles",
    path: `${APP_PREFIX_PATH}/users/roles/views/list-roles`,
    component: React.lazy(() =>
      import("views/app-admin-views/users/roles/views/list-roles")
    ),
  },

  {
    key: "apps.catalogue",
    path: `${APP_PREFIX_PATH}/catalogue/*`,
    component: React.lazy(() => import("views/app-admin-views/catalogue")),
  },
  {
    key: "apps.catalogue.add-product",
    path: `${APP_PREFIX_PATH}/catalogue/add-products`,
    component: React.lazy(() =>
      import("views/app-admin-views/catalogue/add-products")
    ),
  },
  {
    key: "apps.catalogue.edit-product",
    path: `${APP_PREFIX_PATH}/catalogue/edit-products/:id`,
    component: React.lazy(() =>
      import("views/app-admin-views/catalogue/edit-products")
    ),
  },
  {
    key: "apps.catalogue.assign-services-users",
    path: `${APP_PREFIX_PATH}/catalogue/assign-services-users/:id`,
    component: React.lazy(() =>
      import("views/app-admin-views/catalogue/assign-services-users")
    ),
  },
  {
    key: "apps.catalogue.product-list",
    path: `${APP_PREFIX_PATH}/catalogue/list-products`,
    component: React.lazy(() =>
      import("views/app-admin-views/catalogue/list-products")
    ),
  },
  {
    key: "apps.catalogue.masive-upload",
    path: `${APP_PREFIX_PATH}/catalogue/masive-upload`,
    component: React.lazy(() =>
      import("views/app-admin-views/catalogue/masive-upload")
    ),
  },
  {
    key: "apps.catalogue.inventory",
    path: `${APP_PREFIX_PATH}/catalogue/inventory`,
    component: React.lazy(() =>
      import("views/app-admin-views/catalogue/inventory")
    ),
  },
  {
    key: "apps.catalogue.inventory-count",
    path: `${APP_PREFIX_PATH}/catalogue/inventory/inventory-count`,
    component: React.lazy(() =>
      import("views/app-admin-views/catalogue/inventory/views/inventoryCount")
    ),
  },
  {
    key: "apps.catalogue.prices",
    path: `${APP_PREFIX_PATH}/catalogue/prices`,
    component: React.lazy(() =>
      import("views/app-admin-views/catalogue/prices")
    ),
  },
  {
    key: "apps.catalogue.category",
    path: `${APP_PREFIX_PATH}/catalogue/categories`,
    component: React.lazy(() =>
      import("views/app-admin-views/catalogue/categories")
    ),
  },
  {
    key: "apps.catalogue.custom-properties",
    path: `${APP_PREFIX_PATH}/catalogue/custom-properties`,
    component: React.lazy(() =>
      import(
        "views/app-admin-views/catalogue/custom-properties/customProperties"
      )
    ),
  },
  {
    key: "apps.catalogue.recommendations",
    path: `${APP_PREFIX_PATH}/catalogue/recommendations`,
    component: React.lazy(() =>
      import(
        "views/app-admin-views/catalogue/recommendations/listRecommendations"
      )
    ),
  },

  {
    key: "app.taxes",
    path: `${APP_PREFIX_PATH}/taxes/*`,
    component: React.lazy(() => import("views/app-admin-views/taxes")),
  },
  {
    key: "apps.taxes.add-taxes",
    path: `${APP_PREFIX_PATH}/taxes/add-taxes`,
    component: React.lazy(() =>
      import("views/app-admin-views/taxes/add-taxes")
    ),
  },
  {
    key: "apps.taxes.edit-taxes",
    path: `${APP_PREFIX_PATH}/taxes/edit-taxes/:id`,
    component: React.lazy(() =>
      import("views/app-admin-views/taxes/edit-taxes")
    ),
  },
  {
    key: "apps.taxes.taxes-list",
    path: `${APP_PREFIX_PATH}/taxes/list-taxes`,
    component: React.lazy(() =>
      import("views/app-admin-views/taxes/list-taxes")
    ),
  },
  {
    key: "apps.settings",
    path: `${APP_PREFIX_PATH}/settings/`,
    component: React.lazy(() =>
      import("views/app-admin-views/settings/views/settings/AllSettings")
    ),
  },
  {
    key: "apps.settings.generalInfo",
    path: `${APP_PREFIX_PATH}/settings/general-info`,
    component: React.lazy(() =>
      import("views/app-admin-views/settings/views/general-info/GeneralInfo")
    ),
  },
  {
    key: "apps.settings.invoicingConfig",
    path: `${APP_PREFIX_PATH}/settings/configuration-invoicing`,
    component: React.lazy(() =>
      import(
        "views/app-admin-views/settings/views/configuration-invoicing/Invoicing"
      )
    ),
  },
  {
    key: "apps.settings.imageFinder",
    path: `${APP_PREFIX_PATH}/settings/image-finder`,
    component: React.lazy(() =>
      import("views/app-admin-views/settings/views/image-finder/ImageFinder")
    ),
  },
  {
    key: "apps.settings.subscription",
    path: `${APP_PREFIX_PATH}/settings/subscriptions`,
    component: React.lazy(() =>
      import("views/app-admin-views/settings/views/subscriptions/Subscriptions")
    ),
  },
  {
    key: "apps.settings.payment-methods",
    path: `${APP_PREFIX_PATH}/settings/payment-methods`,
    component: React.lazy(() =>
      import("views/app-admin-views/settings/views/settings/PaymentMethods")
    ),
  },
  {
    key: "apps.settings.shipment-rules",
    path: `${APP_PREFIX_PATH}/settings/shipment-rules`,
    component: React.lazy(() =>
      import(
        "views/app-admin-views/settings/views/shipment-rules/OrderShipmentRules"
      )
    ),
  },
  {
    key: "apps.orders",
    path: `${APP_PREFIX_PATH}/orders/*`,
    component: React.lazy(() => import("views/app-admin-views/orders")),
  },
  {
    key: "apps.orders.orders-list",
    path: `${APP_PREFIX_PATH}/orders/list-orders`,
    component: React.lazy(() =>
      import("views/app-admin-views/orders/views/list-orders")
    ),
  },
  {
    key: "apps.orders.orders-detail",
    path: `${APP_PREFIX_PATH}/orders/detail-orders/:id`,
    component: React.lazy(() =>
      import("views/app-admin-views/orders/views/detail-orders")
    ),
  },
  {
    key: "apps.orders.return-orders",
    path: `${APP_PREFIX_PATH}/orders/return-orders/:id`,
    component: React.lazy(() =>
      import("views/app-admin-views/orders/views/return-orders")
    ),
  },
  {
    key: "apps.orders.new-orders",
    path: `${APP_PREFIX_PATH}/orders/new-orders/`,
    component: React.lazy(() =>
      import("views/app-admin-views/orders/views/new-orders")
    ),
  },
  {
    key: "apps.orders.new-orders",
    path: `${APP_PREFIX_PATH}/orders/new-orders-quotation/:id`,
    component: React.lazy(() =>
      import("views/app-admin-views/orders/views/new-orders-quotation")
    ),
  },
  {
    key: "apps.customers",
    path: `${APP_PREFIX_PATH}/customers/*`,
    component: React.lazy(() => import("views/app-admin-views/customers")),
  },
  {
    key: "apps.customers.customers-list",
    path: `${APP_PREFIX_PATH}/customers/list-customers`,
    component: React.lazy(() =>
      import("views/app-admin-views/customers/views/list-customers")
    ),
  },
  {
    key: "apps.customers.list-customers-list",
    path: `${APP_PREFIX_PATH}/customers/list-customers-list`,
    component: React.lazy(() =>
      import(
        "views/app-admin-views/customers/views/CustomersList/CustomersList"
      )
    ),
  },

  {
    key: "apps.customers.masive-upload",
    path: `${APP_PREFIX_PATH}/customers/masive-upload`,
    component: React.lazy(() =>
      import("views/app-admin-views/customers/views/masive-upload")
    ),
  },
  {
    key: "apps.customers.campaings-list",
    path: `${APP_PREFIX_PATH}/customers/list-campaigns`,
    component: React.lazy(() =>
      import(
        "views/app-admin-views/campaigns/views/list-campaigns/ListCampaigns"
      )
    ),
  },
  {
    key: "apps.customers.add-campaing",
    path: `${APP_PREFIX_PATH}/customers/add-campaing`,
    component: React.lazy(() =>
      import(
        "views/app-admin-views/campaigns/views/new-campaign/AddNewCampaign"
      )
    ),
  },
  {
    key: "apps.customers.add-sender",
    path: `${APP_PREFIX_PATH}/customers/add-sender`,
    component: React.lazy(() =>
      import("views/app-admin-views/campaigns/views/new-sender/ValidateSender")
    ),
  },
  {
    key: "apps.customers.customers-detail",
    path: `${APP_PREFIX_PATH}/customers/detail-customers/:id`,
    component: React.lazy(() =>
      import("views/app-admin-views/customers/views/detail-customers")
    ),
  },
  {
    key: "apps.segments.new-segment",
    path: `${APP_PREFIX_PATH}/customers/segments/new-segment`,
    component: React.lazy(() =>
      import("views/app-admin-views/segments/views/new-segment")
    ),
  },
  {
    key: "apps.segments.details",
    path: `${APP_PREFIX_PATH}/customers/segments/details/:id`,
    component: React.lazy(() =>
      import("views/app-admin-views/segments/views/segment-details")
    ),
  },
  {
    key: "apps.segments.list",
    path: `${APP_PREFIX_PATH}/customers/segments`,
    component: React.lazy(() =>
      import("views/app-admin-views/segments/views/list-segments")
    ),
  },
  {
    key: "apps.catalogue.masive-upload",
    path: `${APP_PREFIX_PATH}/customers/segment/masive-upload/:id`,
    component: React.lazy(() =>
      import("views/app-admin-views/segments/views/masive-upload")
    ),
  },

  {
    key: "apps.dashboard",
    path: `${APP_PREFIX_PATH}/dashboards/*`,
    component: React.lazy(() => import("views/app-admin-views/dashboard")),
  },
  {
    key: "apps.dashboard.sales",
    path: `${APP_PREFIX_PATH}/dashboards/sales`,
    component: React.lazy(() =>
      import("views/app-admin-views/dashboard/sales")
    ),
  },
  {
    key: "apps.dashboard.inventory",
    path: `${APP_PREFIX_PATH}/dashboards/inventory-report`,
    component: React.lazy(() =>
      import("views/app-admin-views/dashboard/inventory/views/inventoryReport")
    ),
  },
  {
    key: "apps.dashboard.activities",
    path: `${APP_PREFIX_PATH}/dashboards/activities-report`,
    component: React.lazy(() =>
      import(
        "views/app-admin-views/dashboard/activities/views/activitiesReport"
      )
    ),
  },
  {
    key: "apps.dashboard.quotation",
    path: `${APP_PREFIX_PATH}/dashboards/quotations-report`,
    component: React.lazy(() =>
      import("views/app-admin-views/dashboard/quotations/")
    ),
  },
  {
    key: "apps.dashboard.cash",
    path: `${APP_PREFIX_PATH}/dashboards/cash-closing-report`,
    component: React.lazy(() =>
      import("views/app-admin-views/dashboard/cash/views/listCasherClosed")
    ),
  },
  {
    key: "apps.dashboard.vendor",
    path: `${APP_PREFIX_PATH}/dashboards/vendor-report`,
    component: React.lazy(() =>
      import("views/app-admin-views/dashboard/vendors/views/reportsByVendors")
    ),
  },
  {
    key: "apps.dashboard.vendor-detail",
    path: `${APP_PREFIX_PATH}/dashboards/vendor-detail/:id/:startDate/:finishDate`,
    component: React.lazy(() =>
      import(
        "views/app-admin-views/dashboard/vendors/views/detailReportVendors"
      )
    ),
  },
  {
    key: "apps.home.main",
    path: `${APP_PREFIX_PATH}/home`,
    component: React.lazy(() => import("views/app-admin-views/home")),
  },
  {
    key: "apps.dashboard.commissions-main",
    path: `${APP_PREFIX_PATH}/dashboards/commissions-main`,
    component: React.lazy(() =>
      import("views/app-admin-views/dashboard/commissions/main")
    ),
  },
  {
    key: "apps.dashboard.commissions-detail",
    path: `${APP_PREFIX_PATH}/dashboards/commissions-detail/:id/:start/:finish`,
    component: React.lazy(() =>
      import("views/app-admin-views/dashboard/commissions/details")
    ),
  },

  {
    key: "apps.dashboard.credit",
    path: `${APP_PREFIX_PATH}/credit`,
    component: React.lazy(() =>
      import("views/app-shared-views/credit/views/Credit")
    ),
  },
  {
    key: "apps.vendor.vendor-list",
    path: `${APP_PREFIX_PATH}/vendor/list-vendor`,
    component: React.lazy(() =>
      import("views/app-admin-views/vendor/views/vendor/list-vendor")
    ),
  },

  {
    key: "apps.promotions.promotions-list",
    path: `${APP_PREFIX_PATH}/promotions/list-promotions`,
    component: React.lazy(() =>
      import("views/app-admin-views/promotions/views/ListPromotions")
    ),
  },
  {
    key: "apps.promotions.promotions-type",
    path: `${APP_PREFIX_PATH}/promotions/promotion-type`,
    component: React.lazy(() =>
      import("views/app-admin-views/promotions/views/TypePromotions")
    ),
  },
  {
    key: "apps.promotions.promotions-edit",
    path: `${APP_PREFIX_PATH}/promotions/promotion-edit/:id/:type`,
    component: React.lazy(() =>
      import("views/app-admin-views/promotions/views/NewPromotion")
    ),
  },
  {
    key: "apps.promotions.promotions-create",
    path: `${APP_PREFIX_PATH}/promotions/promotion-create/:type`,
    component: React.lazy(() =>
      import("views/app-admin-views/promotions/views/NewPromotion")
    ),
  },
  {
    key: "apps.vendor.add-vendor",
    path: `${APP_PREFIX_PATH}/vendor/add-vendor`,
    component: React.lazy(() =>
      import("views/app-admin-views/vendor/views/vendor/newVendor")
    ),
  },
  {
    key: "apps.vendor.edit-vendor",
    path: `${APP_PREFIX_PATH}/vendor/edit-vendor/:id`,
    component: React.lazy(() =>
      import("views/app-admin-views/vendor/views/vendor/editVendor")
    ),
  },
  {
    key: "apps.catalogue.inventory",
    path: `${APP_PREFIX_PATH}/purchase/order`,
    component: React.lazy(() =>
      import(
        "views/app-admin-views/vendor/views/purchaseOrder/list-purchase-order"
      )
    ),
  },
  {
    key: "apps.purchaseOrder.add-purchaseOrder",
    path: `${APP_PREFIX_PATH}/purchaseOrder/add-purchase-order`,
    component: React.lazy(() =>
      import(
        "views/app-admin-views/vendor/views/purchaseOrder/newPurchaseOrder"
      )
    ),
  },
  {
    key: "apps.purchaseOrder.edit-purchaseOrder",
    path: `${APP_PREFIX_PATH}/purchaseOrder/edit-purchase-order/:id`,
    component: React.lazy(() =>
      import(
        "views/app-admin-views/vendor/views/purchaseOrder/editPurchaseOrder"
      )
    ),
  },
  {
    key: "apps.purchaseOrder.purchase-order-edit",
    path: `${APP_PREFIX_PATH}/purchaseOrder/:id/edit`,
    component: React.lazy(() =>
      import(
        "views/app-admin-views/vendor/views/purchaseOrder/newPurchaseOrder"
      )
    ),
  },
  {
    key: "apps.manufacturing.raw-material-list",
    path: `${APP_PREFIX_PATH}/manufacturing/list-raw-material`,
    component: React.lazy(() =>
      import(
        "views/app-admin-views/manufacturing/views/raw-materials-list/listRawMaterials"
      )
    ),
  },
  {
    key: "apps.manufacturing.raw-material-new",
    path: `${APP_PREFIX_PATH}/manufacturing/new-raw-material`,
    component: React.lazy(() =>
      import(
        "views/app-admin-views/manufacturing/views/new-raw-materials/newRawMaterials"
      )
    ),
  },
  {
    key: "apps.manufacturing.raw-material-edit",
    path: `${APP_PREFIX_PATH}/manufacturing/edit-raw-material/:id`,
    component: React.lazy(() =>
      import(
        "views/app-admin-views/manufacturing/views/edit-raw-materials/editRawMaterials"
      )
    ),
  },
  {
    key: "apps.manufacturing.raw-material-inventory",
    path: `${APP_PREFIX_PATH}/manufacturing/inventory-raw-material`,
    component: React.lazy(() =>
      import(
        "views/app-admin-views/manufacturing/views/inventory-raw-materials/inventoryRawMaterials"
      )
    ),
  },
  {
    key: "apps.manufacturing.production-orders-new",
    path: `${APP_PREFIX_PATH}/manufacturing/new-production-orders`,
    component: React.lazy(() =>
      import(
        "views/app-admin-views/manufacturing/views/new-production-orders/newProductionOrders"
      )
    ),
  },
  {
    key: "apps.manufacturing.production-orders-edit",
    path: `${APP_PREFIX_PATH}/manufacturing/edit-production-orders/:id`,
    component: React.lazy(() =>
      import(
        "views/app-admin-views/manufacturing/views/edit-production-orders/editProductionOrders"
      )
    ),
  },
  {
    key: "apps.manufacturing.production-orders-list",
    path: `${APP_PREFIX_PATH}/manufacturing/list-production-orders`,
    component: React.lazy(() =>
      import(
        "views/app-admin-views/manufacturing/views/production-orders-list/listProductionOrders"
      )
    ),
  },
  {
    key: "apps.manufacturing.production-orders-list",
    path: `${APP_PREFIX_PATH}/quotat/list-production-orders`,
    component: React.lazy(() =>
      import(
        "views/app-admin-views/manufacturing/views/production-orders-list/listProductionOrders"
      )
    ),
  },

  {
    key: "apps.quotations.quotations-list",
    path: `${APP_PREFIX_PATH}/quotations/list-quotations`,
    component: React.lazy(() =>
      import("views/app-admin-views/quotations/views/list-quotations")
    ),
  },
  {
    key: "apps.quotations.quotations-detail",
    path: `${APP_PREFIX_PATH}/quotations/details-quotation`,
    component: React.lazy(() =>
      import("views/app-admin-views/quotations/views/details-quotation")
    ),
  },
  {
    key: "apps.quotations.create-quotation",
    path: `${APP_PREFIX_PATH}/quotations/create-quotation`,
    component: React.lazy(() =>
      import("views/app-admin-views/quotations/views/create-quotation")
    ),
  },

  {
    key: "apps.quotation.edit-quotataion",
    path: `${APP_PREFIX_PATH}/quotation/edit-quotataion/:id`,
    component: React.lazy(() =>
      import("views/app-admin-views/quotations/views/edit-quotation")
    ),
  },
  {
    key: "apps.quotation.detail-quotataion",
    path: `${APP_PREFIX_PATH}/quotation/details-quotataion/:id`,
    component: React.lazy(() =>
      import("views/app-admin-views/quotations/views/details-quotation")
    ),
  },
  {
    key: "apps.location-activities-create",
    path: `${APP_PREFIX_PATH}/location/activities-create`,
    component: React.lazy(() =>
      import("views/app-admin-views/locations/activities-create")
    ),
  },
  {
    key: "apps.location-activities-create",
    path: `${APP_PREFIX_PATH}/location/activities-edit/:id`,
    component: React.lazy(() =>
      import(
        "views/app-admin-views/locations/activities-edit/editFormActivities"
      )
    ),
  },
  {
    key: "apps.location-activities",
    path: `${APP_PREFIX_PATH}/location/list-activities`,
    component: React.lazy(() =>
      import(
        "views/app-admin-views/locations/list-activities/listActivitiesForm"
      )
    ),
  },
  {
    key: "apps.loyalty",
    path: `${APP_PREFIX_PATH}/loyalty`,
    component: React.lazy(() =>
      import("views/app-admin-views/loyalty/views/loyaltyList")
    ),
  },
  {
    key: "apps.loyalty-list",
    path: `${APP_PREFIX_PATH}/loyalty/list`,
    component: React.lazy(() =>
      import("views/app-admin-views/loyalty/views/loyaltyList")
    ),
  },
  {
    key: "apps.gift-card",
    path: `${APP_PREFIX_PATH}/gift-card/views/listGiftsCards`,
    component: React.lazy(() =>
      import("views/app-admin-views/gift-card/views/listGiftsCards")
    ),
  },

  {
    key: "apps.loyalty-add",
    path: `${APP_PREFIX_PATH}/loyalty/create`,
    component: React.lazy(() =>
      import("views/app-admin-views/loyalty/views/loyaltyAddNew")
    ),
  },
  {
    key: "apps.loyalty-details",
    path: `${APP_PREFIX_PATH}/loyalty/details/:id`,
    component: React.lazy(() =>
      import("views/app-admin-views/loyalty/views/loyaltyDetails")
    ),
  },
  {
    key: "apps.loyalty-updated",
    path: `${APP_PREFIX_PATH}/loyalty/update/:id`,
    component: React.lazy(() =>
      import("views/app-admin-views/loyalty/views/loyaltyUpdate")
    ),
  },
  {
    key: "apps.shipping-transfers",
    path: `${APP_PREFIX_PATH}/shipping-transfers`,
    component: React.lazy(() =>
      import("views/app-admin-views/shipping-transfers")
    ),
  },
  {
    key: "apps.shipping-transfers.shipments",
    path: `${APP_PREFIX_PATH}/shipping-transfers/shipments`,
    component: React.lazy(() =>
      import("views/app-admin-views/shipping-transfers/views/shipments")
    ),
  },
  {
    key: "apps.shipping-transfers.shipment-detail",
    path: `${APP_PREFIX_PATH}/shipping-transfers/shipments/:id`,
    component: React.lazy(() =>
      import("views/app-admin-views/shipping-transfers/views/shipment-detail")
    ),
  },
  {
    key: "apps.shipping-transfers.shipments-massive-preparation",
    path: `${APP_PREFIX_PATH}/shipping-transfers/shipments/massive-preparation/:ids`,
    component: React.lazy(() =>
      import(
        "views/app-admin-views/shipping-transfers/views/shipments-massive-preparation"
      )
    ),
  },
  {
    key: "apps.shipping-transfers.transfers",
    path: `${APP_PREFIX_PATH}/shipping-transfers/transfers`,
    component: React.lazy(() =>
      import("views/app-admin-views/shipping-transfers/views/transfers")
    ),
  },
  {
    key: "apps.shipping-transfers.new-transfer",
    path: `${APP_PREFIX_PATH}/shipping-transfers/transfers/new`,
    component: React.lazy(() =>
      import("views/app-admin-views/shipping-transfers/views/new-transfer")
    ),
  },
  {
    key: "apps.shipping-transfers.transfer-detail",
    path: `${APP_PREFIX_PATH}/shipping-transfers/transfers/:id`,
    component: React.lazy(() =>
      import("views/app-admin-views/shipping-transfers/views/transfer-detail")
    ),
  },
  {
    key: "apps.shipping-transfers.edit-transfer",
    path: `${APP_PREFIX_PATH}/shipping-transfers/transfers/:id/edit`,
    component: React.lazy(() =>
      import("views/app-admin-views/shipping-transfers/views/edit-transfer")
    ),
  },
  {
    key: "apps.shipping-transfers.transporters",
    path: `${APP_PREFIX_PATH}/shipping-transfers/transporters`,
    component: React.lazy(() =>
      import("views/app-admin-views/shipping-transfers/views/transporters")
    ),
  },
  {
    key: "apps.integrations.erps",
    path: `${APP_PREFIX_PATH}/integrations/erps`,
    component: React.lazy(() =>
      import("views/app-admin-views/integrations/erps/views/erps")
    ),
  },
  {
    key: "apps.integrations.erps.sapb1",
    path: `${APP_PREFIX_PATH}/integrations/erps/sapb1`,
    component: React.lazy(() =>
      import("views/app-admin-views/integrations/erps/views/sapb1")
    ),
  },
  {
    key: "apps.integrations.e-commerce",
    path: `${APP_PREFIX_PATH}/ecommerce`,
    component: React.lazy(() =>
      import(
        "views/app-admin-views/integrations/ecommerce/views/list-integrations"
      )
    ),
  },
  {
    key: "apps.integrations.ecommerce.shopify",
    path: `${APP_PREFIX_PATH}/ecommerce/shopify`,
    component: React.lazy(() =>
      import("views/app-admin-views/integrations/ecommerce/views/shopify")
    ),
  },
  {
    key: "apps.resellers.resellers-list",
    path: `${APP_PREFIX_PATH}/resellers/list-resellers`,
    component: React.lazy(() =>
      import("views/app-admin-views/resellers/views/list-resellers")
    ),
  },
  {
    key: "apps.resellers.add-reseller",
    path: `${APP_PREFIX_PATH}/resellers/add`,
    component: React.lazy(() =>
      import("views/app-admin-views/resellers/views/add-resellers")
    ),
  },
  {
    key: "apps.resellers.edit-reseller",
    path: `${APP_PREFIX_PATH}/resellers/edit/:id`,
    component: React.lazy(() =>
      import("views/app-admin-views/resellers/views/edit-resellers")
    ),
  },
  {
    key: "apps.resellers.transfer-reseller",
    path: `${APP_PREFIX_PATH}/resellers/transfer-reseller`,
    component: React.lazy(() =>
      import("views/app-admin-views/resellers/views/transfer-reseller")
    ),
  },
  {
    key: "apps.resellers.transfer-reseller.deliveries-and-returns",
    path: `${APP_PREFIX_PATH}/resellers/deliveries-and-returns`,
    component: React.lazy(() =>
      import(
        "views/app-admin-views/resellers/views/transfer-reseller/deliveries-and-returns"
      )
    ),
  },
  {
    key: "apps.resellers.list-reseller.shipments-and-returns",
    path: `${APP_PREFIX_PATH}/resellers/list-shipments-and-returns`,
    component: React.lazy(() =>
      import("views/app-admin-views/resellers/views/list-shipments-returns")
    ),
  },
  {
    key: "apps.resellers.transfer-detail",
    path: `${APP_PREFIX_PATH}/resellers/transfer-detail/:id`,
    component: React.lazy(() =>
      import("views/app-admin-views/resellers/views/transfer-detail")
    ),
  },
  {
    key: "apps.new.order-reseller",
    path: `${APP_PREFIX_PATH}/resellers/new-order`,
    component: React.lazy(() =>
      import("views/app-admin-views/resellers/views/new-order-reseller")
    ),
  },
  {
    key: "apps.list-segments",
    path: `${APP_PREFIX_PATH}/segments/list-segments`,
    component: React.lazy(() =>
      import("views/app-admin-views/segments/views/list-segments")
    ),
  },
  {
    key: "apps.finance.treasury-accounts",
    path: `${APP_PREFIX_PATH}/finance/banks/treasury-accounts`,
    component: React.lazy(() =>
      import(
        "views/app-admin-views/finance/views/treasury-accounts/treasuryAccounts"
      )
    ),
  },
  {
    key: "apps.finance.treasury-account-details",
    path: `${APP_PREFIX_PATH}/finance/banks/treasury-account-details/:id`,
    component: React.lazy(() =>
      import(
        "views/app-admin-views/finance/views/treasury-account-details/treasuryAccountDetails"
      )
    ),
  },
  {
    key: "apps.finance.payment-rules",
    path: `${APP_PREFIX_PATH}/finance/payment-rules`,
    component: React.lazy(() =>
      import("views/app-admin-views/finance/views/payment-rules/paymentRules")
    ),
  },
];

export const transactionalRoutes = [
  {
    key: "apps.pos.pos-main",
    path: `${APP_NOADMIN_PREFIX_PATH}/pos-main`,
    component: React.lazy(() =>
      import("views/app-noadmin-views/pos/views/pos-main")
    ),
  },
  {
    key: "apps.pos.orders.orders-list",
    path: `${APP_NOADMIN_PREFIX_PATH}/orders/list-orders`,
    component: React.lazy(() =>
      import("views/app-noadmin-views/orders/list-orders")
    ),
  },
  {
    key: "apps.pos.orders.orders-detail",
    path: `${APP_NOADMIN_PREFIX_PATH}/orders/detail-orders/:id`,
    component: React.lazy(() =>
      import("views/app-noadmin-views/orders/detail-orders")
    ),
  },
  {
    key: "apps.pos.orders.return-orders",
    path: `${APP_NOADMIN_PREFIX_PATH}/orders/return-orders/:id`,
    component: React.lazy(() =>
      import("views/app-noadmin-views/orders/views/return-orders")
    ),
  },
  {
    key: "apps.pos.journal",
    path: `${APP_NOADMIN_PREFIX_PATH}/journal/*`,
    component: React.lazy(() => import("views/app-noadmin-views/journal")),
  },
  {
    key: "apps.pos.journal.main",
    path: `${APP_NOADMIN_PREFIX_PATH}/journal/main`,
    component: React.lazy(() => import("views/app-noadmin-views/journal/main")),
  },
  {
    key: "apps.pos.inventory.count",
    path: `${APP_NOADMIN_PREFIX_PATH}/inventory/inventory-count`,
    component: React.lazy(() =>
      import("views/app-noadmin-views/inventory/views/inventoryCount")
    ),
  },
  {
    key: "apps.pos.inventory",
    path: `${APP_NOADMIN_PREFIX_PATH}/inventory/main`,
    component: React.lazy(() =>
      import("views/app-noadmin-views/inventory/views/inventory")
    ),
  },
  {
    key: "apps.pos.dashboard",
    path: `${APP_NOADMIN_PREFIX_PATH}/dashboard/*`,
    component: React.lazy(() => import("views/app-noadmin-views/dashboard")),
  },
  {
    key: "apps.pos.dashboard.main",
    path: `${APP_NOADMIN_PREFIX_PATH}/dashboard/main`,
    component: React.lazy(() =>
      import("views/app-noadmin-views/dashboard/main")
    ),
  },
  {
    key: "apps.shipping-transfers.noadmin",
    path: `${APP_NOADMIN_PREFIX_PATH}/shipping-transfers`,
    component: React.lazy(() =>
      import("views/app-noadmin-views/shipping-transfers")
    ),
  },
  {
    key: "apps.shipping-transfers.noadmin.transfers",
    path: `${APP_NOADMIN_PREFIX_PATH}/shipping-transfers/transfers`,
    component: React.lazy(() =>
      import("views/app-noadmin-views/shipping-transfers/views/transfers")
    ),
  },
  {
    key: "apps.shipping-transfers.noadmin.new-transfer",
    path: `${APP_NOADMIN_PREFIX_PATH}/shipping-transfers/transfers/new`,
    component: React.lazy(() =>
      import("views/app-noadmin-views/shipping-transfers/views/new-transfer")
    ),
  },
  {
    key: "apps.shipping-transfers.noadmin.transfer-detail",
    path: `${APP_NOADMIN_PREFIX_PATH}/shipping-transfers/transfers/:id`,
    component: React.lazy(() =>
      import("views/app-noadmin-views/shipping-transfers/views/transfer-detail")
    ),
  },
  {
    key: "apps.shipping-transfers.noadmin.edit-transfer",
    path: `${APP_NOADMIN_PREFIX_PATH}/shipping-transfers/transfers/:id/edit`,
    component: React.lazy(() =>
      import("views/app-noadmin-views/shipping-transfers/views/edit-transfer")
    ),
  },
  {
    key: "apps.shipping-transfers.noadmin.shipments",
    path: `${APP_NOADMIN_PREFIX_PATH}/shipping-transfers/shipments`,
    component: React.lazy(() =>
      import("views/app-noadmin-views/shipping-transfers/views/shipments")
    ),
  },
  {
    key: "apps.shipping-transfers.noadmin.shipment-detail",
    path: `${APP_NOADMIN_PREFIX_PATH}/shipping-transfers/shipments/:id`,
    component: React.lazy(() =>
      import("views/app-noadmin-views/shipping-transfers/views/shipment-detail")
    ),
  },
  {
    key: "apps.catalogue.noadmin.inventory",
    path: `${APP_NOADMIN_PREFIX_PATH}/purchase/order`,
    component: React.lazy(() =>
      import(
        "views/app-noadmin-views/vendor/views/purchaseOrder/list-purchase-order"
      )
    ),
  },
  {
    key: "apps.purchaseOrder.noadmin.add-purchaseOrder",
    path: `${APP_NOADMIN_PREFIX_PATH}/purchaseOrder/add-purchase-order`,
    component: React.lazy(() =>
      import(
        "views/app-noadmin-views/vendor/views/purchaseOrder/newPurchaseOrder"
      )
    ),
  },
  {
    key: "apps.purchaseOrder.noadmin.edit-purchaseOrder",
    path: `${APP_NOADMIN_PREFIX_PATH}/purchaseOrder/edit-purchase-order/:id`,
    component: React.lazy(() =>
      import(
        "views/app-noadmin-views/vendor/views/purchaseOrder/editPurchaseOrder"
      )
    ),
  },
  {
    key: "apps.purchaseOrder.noadmin.purchase-order-edit",
    path: `${APP_NOADMIN_PREFIX_PATH}/purchaseOrder/:id/edit`,
    component: React.lazy(() =>
      import(
        "views/app-noadmin-views/vendor/views/purchaseOrder/newPurchaseOrder"
      )
    ),
  },
  {
    key: "apps.no-access-permission",
    path: `${APP_PREFIX_PATH}/no-access-permission`,
    component: React.lazy(() =>
      import("views/app-admin-views/no-permission/views/")
    ),
  },
];

export const useFilteredAdminRoutes = () => {
  const { hasAccessToPath } = useUserPermissions();

  const filteredAdminRoutes = adminRoutes.filter((route) =>
    hasAccessToPath(route.path)
  );

  return filteredAdminRoutes;
};

export const protectedRoutes = [...adminRoutes, ...transactionalRoutes];
